import React, { useState } from 'react';

import { Component } from '@paragon/contract-library';

import { PlanPreviewVisuals } from '../components/assistant/PlanPreviewVisuals';
import { useAppSelector } from '../components/hooks';
import {
  calculateComponentBounds,
  getActiveDimensionBounds,
  getFontSizeFromComponentBoundingBox,
} from '../store/selectors/boundsSelectors';
import { getActiveAnalysisSet } from '../store/selectors/getActiveAnalysisSetSelector';
import { getActiveComponentDesign } from '../store/selectors/getActiveComponentDesignSelector';
import { AnalogModelVisual } from './analog/AnalogModelVisual';
import { AnalysisResultContainer } from './analysis/AnalysisResultContainer';
import { ComponentHangersContainer } from './component/ComponentHangersContainer';
import { ComponentVisual } from './component/ComponentVisual';
import { ComponentDesignEditorHandle } from './ComponentDesignEditorHandle';
import { LoadVisuals } from './loading/LoadVisuals';

export function ComponentDesignContainer() {
  const activeComponentDesign = useAppSelector(getActiveComponentDesign);
  const activeAnalysisSet = useAppSelector(getActiveAnalysisSet);
  const dimensionBounds = useAppSelector(getActiveDimensionBounds);

  // We store the edited component here for buttery-smooth visual editing. Updating the Redux state on mouse move is
  // too slow to be smooth.
  const [editedComponent, setEditedComponent] = useState<Component | null>(null);

  const componentInRedux = activeComponentDesign?.component;
  const [prevComponent, setPrevComponent] = useState<Component | undefined>(componentInRedux);
  // Reset the edited component if the component in Redux changes
  if (componentInRedux !== prevComponent) {
    setPrevComponent(componentInRedux);
    setEditedComponent(null);
  }

  const component = editedComponent ?? activeComponentDesign?.component;
  const componentDesignGuid = activeComponentDesign?.guid;

  if (!component || componentDesignGuid == null) return null;

  const componentBoundingBox = calculateComponentBounds(component, false, dimensionBounds);
  const fontSize = getFontSizeFromComponentBoundingBox(componentBoundingBox);
  return (
    <>
      <ComponentVisual
        componentDesignGuid={componentDesignGuid}
        component={component}
        analysisSet={activeAnalysisSet}
        fontSize={fontSize}
      />
      <AnalogModelVisual />
      <LoadVisuals />
      <AnalysisResultContainer />
      <ComponentDesignEditorHandle
        component={component}
        setEditedComponent={setEditedComponent}
        componentDesignGuid={componentDesignGuid}
      />
      <ComponentHangersContainer component={component} />
      {/* Lines as wide as a 2x4 member's depth. */}
      <PlanPreviewVisuals trussGuid={componentDesignGuid} lineWidth={3.5} hatchSpacing={3} />
    </>
  );
}
